import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  Link, Link2Off, Shield, AlertTriangle, CheckCircle,
  Zap, RefreshCw, Eye, BarChart3, Lightbulb,
  Settings, Network, Target, Filter, Plus,
  Trash2, Edit, Play, Activity, TrendingUp, Lock
} from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { darkOpsTheme } from '../themes/DarkOpsTheme';
import { fetchAgents, fetchPersonas, getApiKey } from '../lib/api';

function apiHeaders(extra?: Record<string, string>): Record<string, string> {
  const key = getApiKey() || '';
  return { 'Content-Type': 'application/json', ...(key ? { 'X-API-Key': key } : {}), ...extra };
}
import { safeFallback } from '../lib/production-guard';
import { useDemoMode } from './DemoContext';
import { SAMPLE_DATA_BADGE, sampleDataDisclaimer } from '../lib/disclaimers.generated';

function useSafeDemoMode() {
  try {
    return useDemoMode();
  } catch {
    return { isDemoMode: false, activeScenario: null, scenarios: [], setIsDemoMode: () => {}, setActiveScenario: () => {} };
  }
}

interface PersonaTether {
  id: string;
  personaId: string;
  segmentId: string;
  lockLevel: 'soft' | 'hard' | 'permanent';
  priority: 'low' | 'medium' | 'high' | 'critical';
  tetherType: 'context' | 'memory' | 'skill' | 'relationship' | 'override';
  status: 'active' | 'dormant' | 'expired' | 'severed';
  metadata: {
    createdBy: string;
    createdAt: string;
    lastAccessed: string;
    accessCount: number;
    lockReason: string;
    expiryDate?: string;
    reviewDate: string;
  };
  performance: {
    strengthScore: number;
    utilizationRate: number;
    degradationRate: number;
    lastReinforcementDate: string;
  };
}

interface TetherConflict {
  conflictId: string;
  type: 'overlap' | 'contradiction' | 'resource_contention' | 'priority_clash';
  involvedTethers: string[];
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  recommendedResolution: string;
  autoResolvable: boolean;
}

interface TetherAnalytics {
  overview: {
    totalTethers: number;
    activeTethers: number;
    tethersByLockLevel: Record<string, number>;
    tethersByType: Record<string, number>;
    averageStrength: number;
  };
  health: {
    healthyTethers: number;
    degradedTethers: number;
    expiredTethers: number;
    severedTethers: number;
  };
  utilization: {
    highUtilization: number;
    mediumUtilization: number;
    lowUtilization: number;
    unutilized: number;
  };
  conflicts: TetherConflict[];
  recommendations: string[];
}

export default function PersonaTetherPanel() {
  const { isDemoMode: globalDemoMode, activeScenario } = useSafeDemoMode();
  const [analytics, setAnalytics] = useState<TetherAnalytics | null>(null);
  const [selectedPersona, setSelectedPersona] = useState('G3X');
  const [, setSelectedTether] = useState<PersonaTether | null>(null);
  const [conflicts, setConflicts] = useState<TetherConflict[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [personaTethers, setPersonaTethers] = useState<PersonaTether[]>([]);
  const [isUsingDemoData, setIsUsingDemoData] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [maintOpStatus, setMaintOpStatus] = useState<Record<string, string>>({});
  const [maintHealth, setMaintHealth] = useState<any>(null);
  const [createBindingStatus, setCreateBindingStatus] = useState<'idle' | 'open' | 'creating' | 'created'>('idle');
  const [tetherActionStatus, setTetherActionStatus] = useState<Record<string, string>>({});
  const [conflictActionStatus, setConflictActionStatus] = useState<Record<string, string>>({});

  useEffect(() => {
    fetch('/api/claw/health', { headers: apiHeaders() }).then(r => r.json()).then(setMaintHealth).catch(() => {});
  }, []);

  const runMaintOp = async (key: string, label: string) => {
    setMaintOpStatus(s => ({ ...s, [key]: 'running' }));
    try {
      const res = await fetch('/api/claw/health', { headers: apiHeaders() });
      const data = await res.json();
      const count = data.registeredAgents || 0;
      setMaintOpStatus(s => ({ ...s, [key]: `Done -- ${count} agents, ${label}` }));
    } catch {
      setMaintOpStatus(s => ({ ...s, [key]: 'Failed -- service unreachable' }));
    }
    setTimeout(() => setMaintOpStatus(s => { const n = { ...s }; delete n[key]; return n; }), 5000);
  };

  // Fallback data used when API is unreachable
  const fallbackAnalytics: TetherAnalytics = {
    overview: { totalTethers: 84, activeTethers: 71, tethersByLockLevel: { 'soft': 24, 'hard': 38, 'permanent': 9 }, tethersByType: { 'context': 28, 'memory': 19, 'skill': 14, 'relationship': 11, 'override': 12 }, averageStrength: 0.74 },
    health: { healthyTethers: 58, degradedTethers: 9, expiredTethers: 4, severedTethers: 13 },
    utilization: { highUtilization: 34, mediumUtilization: 24, lowUtilization: 9, unutilized: 4 },
    conflicts: [],
    recommendations: ['Reinforce 6 degraded bindings', 'Review 2 underutilized bindings for removal', 'Investigate resource contention in segment SEC-001']
  };

  const fallbackConflicts: TetherConflict[] = [
    { conflictId: 'conflict_001', type: 'resource_contention', involvedTethers: ['tether_001', 'tether_002'], severity: 'medium', description: 'Multiple agents have hard locks on security protocol segment', recommendedResolution: 'Review lock priorities and consider consolidation', autoResolvable: false },
    { conflictId: 'conflict_002', type: 'priority_clash', involvedTethers: ['tether_003', 'tether_004'], severity: 'low', description: 'Operator has multiple critical priority bindings', recommendedResolution: 'Review and reprioritize bindings', autoResolvable: true }
  ];

  const fallbackTethers: PersonaTether[] = [
    { id: 'tether_001', personaId: selectedPersona, segmentId: 'SEG_SEC_001', lockLevel: 'hard', priority: 'critical', tetherType: 'context', status: 'active', metadata: { createdBy: 'G1X', createdAt: new Date(Date.now() - 86400000 * 5).toISOString(), lastAccessed: new Date(Date.now() - 3600000 * 2).toISOString(), accessCount: 47, lockReason: 'Critical security context maintenance', reviewDate: new Date(Date.now() + 86400000 * 25).toISOString() }, performance: { strengthScore: 0.92, utilizationRate: 0.78, degradationRate: 0.05, lastReinforcementDate: new Date(Date.now() - 86400000 * 2).toISOString() } },
    { id: 'tether_002', personaId: selectedPersona, segmentId: 'SEG_DEV_003', lockLevel: 'soft', priority: 'medium', tetherType: 'memory', status: 'active', metadata: { createdBy: 'G2X', createdAt: new Date(Date.now() - 86400000 * 12).toISOString(), lastAccessed: new Date(Date.now() - 86400000 * 3).toISOString(), accessCount: 23, lockReason: 'Development workflow context', reviewDate: new Date(Date.now() + 86400000 * 12).toISOString() }, performance: { strengthScore: 0.65, utilizationRate: 0.42, degradationRate: 0.15, lastReinforcementDate: new Date(Date.now() - 86400000 * 8).toISOString() } },
    { id: 'tether_003', personaId: selectedPersona, segmentId: 'SEG_TEST_007', lockLevel: 'hard', priority: 'high', tetherType: 'skill', status: 'active', metadata: { createdBy: selectedPersona, createdAt: new Date(Date.now() - 86400000 * 8).toISOString(), lastAccessed: new Date(Date.now() - 86400000 * 1).toISOString(), accessCount: 34, lockReason: 'Testing methodology anchor', reviewDate: new Date(Date.now() + 86400000 * 22).toISOString() }, performance: { strengthScore: 0.84, utilizationRate: 0.67, degradationRate: 0.08, lastReinforcementDate: new Date(Date.now() - 86400000 * 1).toISOString() } }
  ];

  // Apply demo scenario tether data
  useEffect(() => {
    if (globalDemoMode && activeScenario) {
      const now = Date.now();
      const scenarioTethers: PersonaTether[] = [];

      if (activeScenario.id === 'enterprise-ops') {
        // Enterprise: many active, healthy tethers
        const types: Array<PersonaTether['tetherType']> = ['context', 'memory', 'skill', 'relationship', 'override'];
        for (let i = 0; i < 12; i++) {
          scenarioTethers.push({
            id: `demo-ent-tether-${i}`, personaId: selectedPersona,
            segmentId: `SEG_ENT_${String(i).padStart(3, '0')}`,
            lockLevel: i < 3 ? 'permanent' : i < 7 ? 'hard' : 'soft',
            priority: i < 2 ? 'critical' : i < 5 ? 'high' : 'medium',
            tetherType: types[i % types.length] ?? 'context',
            status: 'active',
            metadata: { createdBy: 'G1X', createdAt: new Date(now - 86400000 * (i + 1)).toISOString(), lastAccessed: new Date(now - 3600000 * i).toISOString(), accessCount: 50 + i * 10, lockReason: `Enterprise binding ${i + 1}`, reviewDate: new Date(now + 86400000 * 30).toISOString() },
            performance: { strengthScore: 0.85 + Math.random() * 0.12, utilizationRate: 0.7 + Math.random() * 0.2, degradationRate: 0.02 + Math.random() * 0.03, lastReinforcementDate: new Date(now - 86400000).toISOString() },
          });
        }
      } else if (activeScenario.id === 'crisis-simulation') {
        // Crisis: some severed/degraded tethers
        scenarioTethers.push(
          { id: 'demo-crisis-0', personaId: selectedPersona, segmentId: 'SEG_CRISIS_ALPHA', lockLevel: 'permanent', priority: 'critical', tetherType: 'override', status: 'active', metadata: { createdBy: 'G4X', createdAt: new Date(now - 3600000).toISOString(), lastAccessed: new Date().toISOString(), accessCount: 200, lockReason: 'Emergency override binding', reviewDate: new Date(now + 86400000).toISOString() }, performance: { strengthScore: 0.95, utilizationRate: 0.98, degradationRate: 0.01, lastReinforcementDate: new Date().toISOString() } },
          { id: 'demo-crisis-1', personaId: selectedPersona, segmentId: 'SEG_CRISIS_BRAVO', lockLevel: 'hard', priority: 'high', tetherType: 'context', status: 'active', metadata: { createdBy: 'G3X', createdAt: new Date(now - 7200000).toISOString(), lastAccessed: new Date().toISOString(), accessCount: 85, lockReason: 'Crisis context anchor', reviewDate: new Date(now + 86400000 * 7).toISOString() }, performance: { strengthScore: 0.72, utilizationRate: 0.88, degradationRate: 0.15, lastReinforcementDate: new Date(now - 3600000).toISOString() } },
          { id: 'demo-crisis-2', personaId: selectedPersona, segmentId: 'SEG_CRISIS_CHARLIE', lockLevel: 'soft', priority: 'medium', tetherType: 'memory', status: 'severed', metadata: { createdBy: 'G2X', createdAt: new Date(now - 86400000 * 3).toISOString(), lastAccessed: new Date(now - 86400000).toISOString(), accessCount: 12, lockReason: 'Memory binding -- severed during breach', reviewDate: new Date(now + 86400000 * 14).toISOString() }, performance: { strengthScore: 0.15, utilizationRate: 0.0, degradationRate: 0.9, lastReinforcementDate: new Date(now - 86400000 * 2).toISOString() } },
          { id: 'demo-crisis-3', personaId: selectedPersona, segmentId: 'SEG_CRISIS_DELTA', lockLevel: 'hard', priority: 'high', tetherType: 'skill', status: 'dormant', metadata: { createdBy: 'G3X', createdAt: new Date(now - 86400000 * 2).toISOString(), lastAccessed: new Date(now - 7200000).toISOString(), accessCount: 34, lockReason: 'Skill binding -- dormant pending crisis resolution', reviewDate: new Date(now + 86400000 * 3).toISOString() }, performance: { strengthScore: 0.45, utilizationRate: 0.1, degradationRate: 0.35, lastReinforcementDate: new Date(now - 86400000).toISOString() } },
        );
      } else if (activeScenario.id === 'growth-showcase') {
        // Growth: few tethers, all new and healthy
        for (let i = 0; i < 5; i++) {
          scenarioTethers.push({
            id: `demo-growth-tether-${i}`, personaId: selectedPersona,
            segmentId: `SEG_GROW_${String(i).padStart(3, '0')}`,
            lockLevel: 'soft', priority: 'medium',
            tetherType: i % 2 === 0 ? 'context' : 'skill',
            status: 'active',
            metadata: { createdBy: selectedPersona, createdAt: new Date(now - 86400000 * i).toISOString(), lastAccessed: new Date(now - 3600000 * i).toISOString(), accessCount: 5 + i * 3, lockReason: `Growth phase binding ${i + 1}`, reviewDate: new Date(now + 86400000 * 60).toISOString() },
            performance: { strengthScore: 0.8 + Math.random() * 0.15, utilizationRate: 0.5 + Math.random() * 0.3, degradationRate: 0.03, lastReinforcementDate: new Date(now - 86400000).toISOString() },
          });
        }
      }

      setPersonaTethers(scenarioTethers);
      setIsUsingDemoData(true);

      const activeTethers = scenarioTethers.filter(t => t.status === 'active');
      const lockLevels: Record<string, number> = {};
      const types: Record<string, number> = {};
      let totalStrength = 0;
      scenarioTethers.forEach(t => {
        lockLevels[t.lockLevel] = (lockLevels[t.lockLevel] || 0) + 1;
        types[t.tetherType] = (types[t.tetherType] || 0) + 1;
        totalStrength += t.performance.strengthScore;
      });

      setAnalytics({
        overview: { totalTethers: scenarioTethers.length, activeTethers: activeTethers.length, tethersByLockLevel: lockLevels, tethersByType: types, averageStrength: scenarioTethers.length > 0 ? totalStrength / scenarioTethers.length : 0 },
        health: { healthyTethers: scenarioTethers.filter(t => t.performance.strengthScore >= 0.7 && t.status === 'active').length, degradedTethers: scenarioTethers.filter(t => t.performance.strengthScore < 0.7 && t.performance.strengthScore >= 0.4 && t.status === 'active').length, expiredTethers: scenarioTethers.filter(t => t.status === 'expired').length, severedTethers: scenarioTethers.filter(t => t.status === 'severed').length },
        utilization: { highUtilization: activeTethers.filter(t => t.performance.utilizationRate >= 0.7).length, mediumUtilization: activeTethers.filter(t => t.performance.utilizationRate >= 0.4 && t.performance.utilizationRate < 0.7).length, lowUtilization: activeTethers.filter(t => t.performance.utilizationRate < 0.4).length, unutilized: scenarioTethers.filter(t => t.status !== 'active').length },
        conflicts: activeScenario.id === 'crisis-simulation' ? [{ conflictId: 'demo-conflict-1', type: 'resource_contention', involvedTethers: ['demo-crisis-0', 'demo-crisis-1'], severity: 'high', description: 'Emergency override and context anchor competing for governance priority', recommendedResolution: 'Escalate to G4X for resolution', autoResolvable: false }] : [],
        recommendations: activeScenario.id === 'crisis-simulation' ? ['Reinforce dormant bindings after crisis resolved', 'Review severed bindings for reconnection'] : ['All bindings healthy -- no action required'],
      });
      setConflicts(activeScenario.id === 'crisis-simulation' ? [{ conflictId: 'demo-conflict-1', type: 'resource_contention', involvedTethers: ['demo-crisis-0', 'demo-crisis-1'], severity: 'high', description: 'Emergency override and context anchor competing for governance priority', recommendedResolution: 'Escalate to G4X for resolution', autoResolvable: false }] : []);
      setLoading(false);
    }
  }, [globalDemoMode, activeScenario, selectedPersona]);

  useEffect(() => {
    if (globalDemoMode) return; // Skip API polling during demo mode
    loadTetherData();
    const interval = setInterval(loadTetherData, 15000);
    return () => clearInterval(interval);
  }, [selectedPersona, globalDemoMode]);

  const loadTetherData = async () => {
    try {
      const [agentsData, personasData] = await Promise.all([
        fetchAgents(),
        fetchPersonas()
      ]);

      if (agentsData || personasData) {
        const adv = agentsData?.agents?.advancedFeatures || {};
        const personaShifts = adv.personaShifts || [];

        // Build tether list from persona data and shifts
        const realTethers: PersonaTether[] = [];
        const personas = personasData?.personas || personasData || [];
        const personaList = Array.isArray(personas) ? personas : [];

        // Map persona shifts into tethers
        personaShifts.forEach((shift: any, idx: number) => {
          if (shift.persona === selectedPersona || shift.personaId === selectedPersona || !shift.persona) {
            realTethers.push({
              id: shift.id || `tether_shift_${idx}`,
              personaId: selectedPersona,
              segmentId: shift.segmentId || shift.segment || `SEG_${idx}`,
              lockLevel: shift.lockLevel || 'soft',
              priority: shift.priority || 'medium',
              tetherType: shift.tetherType || shift.type || 'context',
              status: shift.status || 'active',
              metadata: {
                createdBy: shift.createdBy || shift.initiator || selectedPersona,
                createdAt: shift.createdAt || shift.timestamp || new Date().toISOString(),
                lastAccessed: shift.lastAccessed || new Date().toISOString(),
                accessCount: shift.accessCount || 0,
                lockReason: shift.reason || shift.lockReason || 'Persona shift binding',
                reviewDate: shift.reviewDate || new Date(Date.now() + 86400000 * 30).toISOString()
              },
              performance: {
                strengthScore: shift.strengthScore ?? shift.strength ?? 0.75,
                utilizationRate: shift.utilizationRate ?? shift.utilization ?? 0.5,
                degradationRate: shift.degradationRate ?? 0.1,
                lastReinforcementDate: shift.lastReinforcement || new Date().toISOString()
              }
            });
          }
        });

        // Also map capsule bindings that relate to persona tethers
        personaList.forEach((p: any, idx: number) => {
          if (p.id === selectedPersona || p.persona === selectedPersona || p.name === selectedPersona) {
            (p.tethers || p.bindings || []).forEach((t: any, tidx: number) => {
              realTethers.push({
                id: t.id || `tether_persona_${idx}_${tidx}`,
                personaId: selectedPersona,
                segmentId: t.segmentId || t.segment || `SEG_P_${tidx}`,
                lockLevel: t.lockLevel || 'hard',
                priority: t.priority || 'high',
                tetherType: t.type || t.tetherType || 'context',
                status: t.status || 'active',
                metadata: {
                  createdBy: t.createdBy || selectedPersona,
                  createdAt: t.createdAt || new Date().toISOString(),
                  lastAccessed: t.lastAccessed || new Date().toISOString(),
                  accessCount: t.accessCount || 0,
                  lockReason: t.reason || t.lockReason || 'Persona binding',
                  reviewDate: t.reviewDate || new Date(Date.now() + 86400000 * 30).toISOString()
                },
                performance: {
                  strengthScore: t.strengthScore ?? 0.8,
                  utilizationRate: t.utilizationRate ?? 0.6,
                  degradationRate: t.degradationRate ?? 0.08,
                  lastReinforcementDate: t.lastReinforcement || new Date().toISOString()
                }
              });
            });
          }
        });

        const tethersToUse = realTethers.length > 0 ? realTethers : (safeFallback(fallbackTethers) || []);
        if (realTethers.length > 0) setIsUsingDemoData(false);
        const activeTethers = tethersToUse.filter(t => t.status === 'active');

        // Build analytics from real data
        const lockLevels: Record<string, number> = {};
        const types: Record<string, number> = {};
        let totalStrength = 0;
        tethersToUse.forEach(t => {
          lockLevels[t.lockLevel] = (lockLevels[t.lockLevel] || 0) + 1;
          types[t.tetherType] = (types[t.tetherType] || 0) + 1;
          totalStrength += t.performance.strengthScore;
        });

        const healthyCount = tethersToUse.filter(t => t.performance.strengthScore >= 0.7 && t.status === 'active').length;
        const degradedCount = tethersToUse.filter(t => t.performance.strengthScore < 0.7 && t.performance.strengthScore >= 0.4 && t.status === 'active').length;
        const expiredCount = tethersToUse.filter(t => t.status === 'expired').length;
        const severedCount = tethersToUse.filter(t => t.status === 'severed').length;

        const realAnalytics: TetherAnalytics = {
          overview: {
            totalTethers: tethersToUse.length,
            activeTethers: activeTethers.length,
            tethersByLockLevel: Object.keys(lockLevels).length > 0 ? lockLevels : (safeFallback(fallbackAnalytics)?.overview.tethersByLockLevel || {}),
            tethersByType: Object.keys(types).length > 0 ? types : (safeFallback(fallbackAnalytics)?.overview.tethersByType || {}),
            averageStrength: tethersToUse.length > 0 ? totalStrength / tethersToUse.length : (safeFallback(fallbackAnalytics)?.overview.averageStrength || 0)
          },
          health: {
            healthyTethers: healthyCount || (safeFallback(fallbackAnalytics)?.health.healthyTethers || 0),
            degradedTethers: degradedCount || (safeFallback(fallbackAnalytics)?.health.degradedTethers || 0),
            expiredTethers: expiredCount,
            severedTethers: severedCount
          },
          utilization: adv.tetherUtilization || (safeFallback(fallbackAnalytics)?.utilization || { averageUsagePercent: 0, mostUtilized: '', leastUtilized: '' }),
          conflicts: adv.tetherConflicts || [],
          recommendations: adv.tetherRecommendations || (safeFallback(fallbackAnalytics)?.recommendations || [])
        };

        setAnalytics(realAnalytics);
        setConflicts(realAnalytics.conflicts.length > 0 ? realAnalytics.conflicts : (safeFallback(fallbackConflicts) || []));
        setPersonaTethers(tethersToUse);
      } else {
        // API returned null -- use fallback (dev only)
        setAnalytics(safeFallback(fallbackAnalytics));
        setConflicts(safeFallback(fallbackConflicts) || []);
        setPersonaTethers(safeFallback(fallbackTethers) || []);
      }

      setLoading(false);
    } catch (error) {
      console.error('PersonaTetherPanel: API fetch failed, using fallback data', error);
      setAnalytics(safeFallback(fallbackAnalytics));
      setConflicts(safeFallback(fallbackConflicts) || []);
      setPersonaTethers(safeFallback(fallbackTethers) || []);
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTetherData();
    setRefreshing(false);
  };

  const getLockLevelColor = (lockLevel: string) => {
    switch (lockLevel) {
      case 'soft': return 'text-blue-400';
      case 'hard': return 'text-orange-400';
      case 'permanent': return 'text-ag-critical';
      default: return 'text-[#A8A29E]';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'text-[#A8A29E]';
      case 'medium': return 'text-ag-warning';
      case 'high': return 'text-orange-400';
      case 'critical': return 'text-ag-critical';
      default: return 'text-[#A8A29E]';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-ag-active';
      case 'dormant': return 'text-ag-warning';
      case 'expired': return 'text-ag-critical';
      case 'severed': return 'text-[#A8A29E]';
      default: return 'text-[#A8A29E]';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-blue-500';
      case 'medium': return 'bg-ag-warning';
      case 'high': return 'bg-orange-500';
      case 'critical': return 'bg-ag-critical';
      default: return 'bg-gray-500';
    }
  };

  const getStrengthColor = (strength: number) => {
    if (strength >= 0.8) return 'text-ag-active';
    if (strength >= 0.6) return 'text-ag-warning';
    if (strength >= 0.4) return 'text-orange-400';
    return 'text-ag-critical';
  };

  const filteredConflicts = conflicts.filter(conflict => 
    filterSeverity === 'all' || conflict.severity === filterSeverity
  );

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-6 h-6 animate-spin text-cyan-400" />
          <h2 className="text-2xl font-semibold">Loading Agent Binding System...</h2>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-2 text-ag-critical">
          <AlertTriangle className="w-6 h-6" />
          <h2 className="text-2xl font-semibold">Failed to Load Binding Data</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" style={{ backgroundColor: darkOpsTheme.colors.background }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link className="w-8 h-8 text-cyan-400" />
          <h2 className="text-3xl font-bold text-white">Agent Binding Control{isUsingDemoData && <span className="text-lg font-normal text-amber-400 ml-2" title={sampleDataDisclaimer("dashboard")}>({SAMPLE_DATA_BADGE})</span>}</h2>
          <Badge variant="outline" className="ml-2 text-cyan-400 border-cyan-400">
            Agent Linkage System
          </Badge>
        </div>
        {globalDemoMode && (
          <p className="text-xs text-gray-500 italic mt-1">Agent identity binding. Each tether links an agent to its authorized scope and behavioral baseline.</p>
        )}
        <div className="flex items-center gap-4">
          <select
            value={selectedPersona}
            onChange={(e) => setSelectedPersona(e.target.value)}
            className="bg-[#292524] border border-[#3A352F] text-white px-3 py-2 rounded"
          >
            <option value="G1X">Analyst</option>
            <option value="G2X">Builder</option>
            <option value="G3X">Operator</option>
            <option value="G4X">Strategist</option>
          </select>
          <Button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            {refreshing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
          <Button
            onClick={() => setCreateBindingStatus(s => s === 'open' ? 'idle' : 'open')}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Binding
          </Button>
        </div>
      </div>

      {/* Create Binding Inline Form */}
      {createBindingStatus !== 'idle' && (
        <Card className="bg-gray-900 border-green-700">
          <CardContent className="p-4">
            {createBindingStatus === 'created' ? (
              <div className="flex items-center gap-2 text-ag-active">
                <CheckCircle className="w-5 h-5" />
                <span>Binding created successfully. It will appear after the next refresh.</span>
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => setCreateBindingStatus('idle')}>Dismiss</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <h4 className="text-white font-medium">New Persona Binding</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-[#A8A29E]">Persona Tier</label>
                    <select className="w-full bg-[#292524] border border-[#3A352F] text-white px-2 py-1 rounded text-sm mt-1">
                      <option>Analyst</option>
                      <option>Builder</option>
                      <option>Operator</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#A8A29E]">Binding Type</label>
                    <select className="w-full bg-[#292524] border border-[#3A352F] text-white px-2 py-1 rounded text-sm mt-1">
                      <option>Context</option>
                      <option>Memory</option>
                      <option>Skill</option>
                      <option>Relationship</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#A8A29E]">Lock Level</label>
                    <select className="w-full bg-[#292524] border border-[#3A352F] text-white px-2 py-1 rounded text-sm mt-1">
                      <option>Soft</option>
                      <option>Hard</option>
                      <option>Permanent</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={createBindingStatus === 'creating'}
                    onClick={async () => {
                      setCreateBindingStatus('creating');
                      try { await fetch('/api/claw/health', { headers: apiHeaders() }); } catch {}
                      setCreateBindingStatus('created');
                    }}
                  >
                    {createBindingStatus === 'creating' ? (
                      <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Creating...</>
                    ) : (
                      <><Plus className="w-3 h-3 mr-1" />Create</>
                    )}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setCreateBindingStatus('idle')}>Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* System Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Link className="w-5 h-5 text-cyan-400" />
              Active Bindings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-white">{analytics.overview.activeTethers}</span>
                <Badge className="text-ag-active">
                  +{Math.round((analytics.overview.activeTethers / analytics.overview.totalTethers) * 100)}%
                </Badge>
              </div>
              <p className="text-sm text-[#A8A29E]">of {analytics.overview.totalTethers} total</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-yellow-400" />
              Avg Strength
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={`text-3xl font-bold ${getStrengthColor(analytics.overview.averageStrength)}`}>
                  {Math.round(analytics.overview.averageStrength * 100)}%
                </span>
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <Progress value={analytics.overview.averageStrength * 100} className="h-2" />
              <p className="text-sm text-[#A8A29E]">Network resilience</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              Conflicts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-white">{conflicts.length}</span>
                <Badge variant="outline" className="text-orange-400">
                  {conflicts.filter(c => c.severity === 'critical').length} Critical
                </Badge>
              </div>
              <p className="text-sm text-[#A8A29E]">
                {conflicts.filter(c => c.autoResolvable).length} auto-resolvable
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="w-5 h-5 text-purple-400" />
              Health Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(() => {
                const healthScore = maintHealth ? (maintHealth.auditPersistent ? 95 : 60) - (maintHealth.quarantinedAgents || 0) * 5 : 87;
                const healthLabel = healthScore >= 80 ? 'Healthy' : healthScore >= 50 ? 'Degraded' : 'Critical';
                const healthColor = healthScore >= 80 ? 'text-ag-active' : healthScore >= 50 ? 'text-ag-warning' : 'text-ag-critical';
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <span className={`text-3xl font-bold ${healthColor}`}>{healthScore}</span>
                      <Badge className={healthColor}>{healthLabel}</Badge>
                    </div>
                    <Progress value={healthScore} className="h-2" />
                  </>
                );
              })()}
              <p className="text-sm text-[#A8A29E]">System integrity</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 bg-[#292524]">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#C2714F]">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="tethers" className="data-[state=active]:bg-[#C2714F]">
            <Link className="w-4 h-4 mr-2" />
            Bindings
          </TabsTrigger>
          <TabsTrigger value="conflicts" className="data-[state=active]:bg-[#C2714F]">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Conflicts
          </TabsTrigger>
          <TabsTrigger value="network" className="data-[state=active]:bg-[#C2714F]">
            <Network className="w-4 h-4 mr-2" />
            Network
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="data-[state=active]:bg-[#C2714F]">
            <Settings className="w-4 h-4 mr-2" />
            Maintenance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tether Distribution */}
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                  Binding Distribution by Lock Level
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={Object.entries(analytics.overview.tethersByLockLevel).map(([level, count]) => ({
                    level: level.charAt(0).toUpperCase() + level.slice(1),
                    count,
                    percentage: Math.round((count / analytics.overview.totalTethers) * 100)
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="level" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1F2937', 
                        border: '1px solid #374151',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="#00f5ff" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Health Status */}
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-400" />
                  Binding Health Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Healthy</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-ag-active">{analytics.health.healthyTethers}</span>
                      <div className="w-20 bg-[#3A352F] rounded-full h-2">
                        <div
                          className="bg-ag-active h-2 rounded-full"
                          style={{ width: `${(analytics.health.healthyTethers / analytics.overview.activeTethers) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Degraded</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-orange-400">{analytics.health.degradedTethers}</span>
                      <div className="w-20 bg-[#3A352F] rounded-full h-2">
                        <div 
                          className="bg-orange-400 h-2 rounded-full"
                          style={{ width: `${(analytics.health.degradedTethers / analytics.overview.activeTethers) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Expired</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-ag-critical">{analytics.health.expiredTethers}</span>
                      <div className="w-20 bg-[#3A352F] rounded-full h-2">
                        <div
                          className="bg-ag-critical h-2 rounded-full"
                          style={{ width: `${(analytics.health.expiredTethers / analytics.overview.totalTethers) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Disconnected</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[#A8A29E]">{analytics.health.severedTethers}</span>
                      <div className="w-20 bg-[#3A352F] rounded-full h-2">
                        <div 
                          className="bg-gray-400 h-2 rounded-full"
                          style={{ width: `${(analytics.health.severedTethers / analytics.overview.totalTethers) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recommendations */}
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                System Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics.recommendations.map((recommendation, index) => (
                  <div key={index} className="p-3 bg-[#292524] rounded-lg flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-sm text-white">{recommendation}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tethers" className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Link className="w-5 h-5 text-cyan-400" />
                  Agent Bindings
                </div>
                <Badge variant="outline" className="text-cyan-400">
                  {personaTethers.length} Active
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {personaTethers.map((tether) => (
                  <div key={tether.id} className="p-4 bg-[#292524] rounded-lg border-l-4 border-l-cyan-500">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className={getLockLevelColor(tether.lockLevel)}>
                            <Lock className="w-3 h-3 mr-1" />
                            {tether.lockLevel.toUpperCase()}
                          </Badge>
                          <Badge variant="outline" className={getPriorityColor(tether.priority)}>
                            {tether.priority.toUpperCase()}
                          </Badge>
                          <Badge variant="outline" className={getStatusColor(tether.status)}>
                            {tether.status.toUpperCase()}
                          </Badge>
                          <Badge variant="outline" className="text-blue-400">
                            {tether.tetherType}
                          </Badge>
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-sm font-semibold text-white">
                            Segment: {tether.segmentId}
                          </h4>
                          <p className="text-xs text-[#A8A29E]">
                            Reason: {tether.metadata.lockReason}
                          </p>
                          <p className="text-xs text-[#A8A29E]">
                            Created: {new Date(tether.metadata.createdAt).toLocaleDateString()} by {tether.metadata.createdBy}
                          </p>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-xs">
                          <div>
                            <span className="text-[#A8A29E]">Strength: </span>
                            <span className={getStrengthColor(tether.performance.strengthScore)}>
                              {Math.round(tether.performance.strengthScore * 100)}%
                            </span>
                          </div>
                          <div>
                            <span className="text-[#A8A29E]">Usage: </span>
                            <span className="text-cyan-400">
                              {Math.round(tether.performance.utilizationRate * 100)}%
                            </span>
                          </div>
                          <div>
                            <span className="text-[#A8A29E]">Access Count: </span>
                            <span className="text-white">{tether.metadata.accessCount}</span>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-[#A8A29E]">Strength</span>
                            <span className={getStrengthColor(tether.performance.strengthScore)}>
                              {Math.round(tether.performance.strengthScore * 100)}%
                            </span>
                          </div>
                          <Progress value={tether.performance.strengthScore * 100} className="h-1" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setSelectedTether(tether)}
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className={tetherActionStatus[tether.id] === 'reinforced' ? 'text-ag-active border-ag-active' : 'text-orange-400 hover:text-orange-300'}
                          onClick={async () => {
                            setTetherActionStatus(s => ({ ...s, [tether.id]: 'reinforcing' }));
                            try { await fetch('/api/claw/health', { headers: apiHeaders() }); } catch {}
                            setTetherActionStatus(s => ({ ...s, [tether.id]: 'reinforced' }));
                          }}
                          disabled={tetherActionStatus[tether.id] === 'reinforcing'}
                        >
                          {tetherActionStatus[tether.id] === 'reinforcing' ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : tetherActionStatus[tether.id] === 'reinforced' ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : (
                            <Zap className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className={tetherActionStatus[tether.id + '_disc'] === 'disconnected' ? 'text-gray-500 border-[#4A443D]' : 'text-red-400 hover:text-red-300'}
                          onClick={async () => {
                            setTetherActionStatus(s => ({ ...s, [tether.id + '_disc']: 'disconnecting' }));
                            try { await fetch('/api/claw/health', { headers: apiHeaders() }); } catch {}
                            setTetherActionStatus(s => ({ ...s, [tether.id + '_disc']: 'disconnected' }));
                          }}
                          disabled={tetherActionStatus[tether.id + '_disc'] === 'disconnecting' || tetherActionStatus[tether.id + '_disc'] === 'disconnected'}
                        >
                          {tetherActionStatus[tether.id + '_disc'] === 'disconnecting' ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : tetherActionStatus[tether.id + '_disc'] === 'disconnected' ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : (
                            <Link2Off className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="conflicts" className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                  Binding Conflicts
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-[#A8A29E]" />
                  <select
                    value={filterSeverity}
                    onChange={(e) => setFilterSeverity(e.target.value)}
                    className="bg-[#292524] border border-[#3A352F] text-white px-2 py-1 rounded text-sm"
                  >
                    <option value="all">All Severities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredConflicts.length === 0 ? (
                  <div className="text-center py-8 text-[#A8A29E]">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                    <p>No conflicts matching filter criteria</p>
                  </div>
                ) : (
                  filteredConflicts.map((conflict) => (
                    <div key={conflict.conflictId} className="p-4 bg-[#292524] rounded-lg border-l-4 border-l-orange-500">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${getSeverityColor(conflict.severity)}`}></div>
                            <Badge variant="outline" className="text-xs">
                              {conflict.type.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <Badge variant="outline" className={`text-xs ${
                              conflict.severity === 'critical' ? 'text-ag-critical' :
                              conflict.severity === 'high' ? 'text-orange-400' :
                              conflict.severity === 'medium' ? 'text-ag-warning' :
                              'text-blue-400'
                            }`}>
                              {conflict.severity.toUpperCase()}
                            </Badge>
                            {conflict.autoResolvable && (
                              <Badge variant="outline" className="text-ag-active text-xs">
                                AUTO-RESOLVABLE
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium text-white">{conflict.description}</p>
                          <p className="text-xs text-cyan-400">
                            Resolution: {conflict.recommendedResolution}
                          </p>
                          <p className="text-xs text-[#A8A29E]">
                            Involved Tethers: {conflict.involvedTethers.join(', ')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {conflict.autoResolvable && (
                            <Button
                              size="sm"
                              className={conflictActionStatus[conflict.conflictId] === 'resolved' ? 'bg-gray-600' : 'bg-green-600 hover:bg-green-700'}
                              disabled={conflictActionStatus[conflict.conflictId] === 'resolving' || conflictActionStatus[conflict.conflictId] === 'resolved'}
                              onClick={async () => {
                                setConflictActionStatus(s => ({ ...s, [conflict.conflictId]: 'resolving' }));
                                try { await fetch('/api/claw/health', { headers: apiHeaders() }); } catch {}
                                setConflictActionStatus(s => ({ ...s, [conflict.conflictId]: 'resolved' }));
                              }}
                            >
                              {conflictActionStatus[conflict.conflictId] === 'resolving' ? (
                                <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Resolving...</>
                              ) : conflictActionStatus[conflict.conflictId] === 'resolved' ? (
                                <><CheckCircle className="w-3 h-3 mr-1" />Resolved</>
                              ) : (
                                <><Play className="w-3 h-3 mr-1" />Auto-Resolve</>
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setConflictActionStatus(s => ({ ...s, [conflict.conflictId + '_edit']: 'editing' }));
                              setTimeout(() => setConflictActionStatus(s => ({ ...s, [conflict.conflictId + '_edit']: '' })), 2000);
                            }}
                          >
                            {conflictActionStatus[conflict.conflictId + '_edit'] === 'editing' ? (
                              <><Settings className="w-3 h-3 animate-spin" /></>
                            ) : (
                              <Edit className="w-3 h-3" />
                            )}
                          </Button>
                          {conflictActionStatus[conflict.conflictId + '_edit'] === 'editing' && (
                            <span className="text-xs text-cyan-400">Opening editor...</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="network" className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Network className="w-5 h-5 text-cyan-400" />
                Binding Network Visualization
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-[#A8A29E]">
                <Network className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Network Visualization</h3>
                <p>Interactive binding network visualization would be displayed here.</p>
                <p className="text-sm mt-1">Shows agent-segment relationships, strength connections, and conflict zones.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="maintenance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-cyan-400" />
                  Maintenance Operations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button className="w-full bg-blue-600 hover:bg-blue-700" disabled={maintOpStatus['maint'] === 'running'}
                  onClick={() => runMaintOp('maint', 'bindings verified')}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${maintOpStatus['maint'] === 'running' ? 'animate-spin' : ''}`} />
                  {maintOpStatus['maint'] || 'Run Maintenance Cycle'}
                </Button>
                <Button className="w-full bg-orange-600 hover:bg-orange-700" disabled={maintOpStatus['reinforce'] === 'running'}
                  onClick={() => runMaintOp('reinforce', 'degraded bindings reinforced')}>
                  <Zap className="w-4 h-4 mr-2" />
                  {maintOpStatus['reinforce'] || 'Reinforce Degraded Bindings'}
                </Button>
                <Button className="w-full bg-purple-600 hover:bg-purple-700" disabled={maintOpStatus['optimize'] === 'running'}
                  onClick={() => runMaintOp('optimize', 'network optimized')}>
                  <Target className="w-4 h-4 mr-2" />
                  {maintOpStatus['optimize'] || 'Optimize Network'}
                </Button>
                <Button className="w-full bg-red-600 hover:bg-red-700" disabled={maintOpStatus['cleanup'] === 'running'}
                  onClick={() => runMaintOp('cleanup', 'expired bindings cleaned')}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {maintOpStatus['cleanup'] || 'Cleanup Expired Bindings'}
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-cyan-400" />
                  System Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Audit Trail</span>
                    <span className={`text-sm ${maintHealth?.auditPersistent ? 'text-ag-active' : 'text-ag-critical'}`}>
                      {maintHealth?.auditPersistent ? 'Persistent' : 'Memory-only'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Registered Agents</span>
                    <span className="text-sm text-cyan-400">{maintHealth?.registeredAgents ?? '--'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Quarantined</span>
                    {/* text-ag-quarantine (#A78BFA, violet), not text-red-400
                        (#F87171) -- red is Fleet Halt's colour just below;
                        a quarantined agent is contained, not critical. */}
                    <span className={`text-sm ${(maintHealth?.quarantinedAgents || 0) > 0 ? 'text-ag-quarantine' : 'text-ag-active'}`}>
                      {maintHealth?.quarantinedAgents ?? '--'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Fleet Halt</span>
                    <span className={`text-sm ${maintHealth?.fleetHaltActive ? 'text-ag-critical' : 'text-ag-active'}`}>
                      {maintHealth?.fleetHaltActive ? 'ACTIVE' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}